import React, { useState } from 'react';
import { IonButton, IonActionSheet } from '@ionic/react';
import {
  logOutOutline,
  peopleOutline,
  settingsOutline,
  personOutline,
  personCircleOutline,
  helpCircleOutline,
  informationCircleOutline,
  paperPlaneOutline,
} from 'ionicons/icons';
import { useLocation } from 'react-router-dom';
import { useIonRouter } from '@ionic/react';
import { useUserContext } from '@/modules/user/useUserContext';
import { ClaimsLogic } from '@/modules/idv';
import { useAppConfig, pxToRem } from '@/modules/config';
import { topicGardenEnabled } from '@/conditional_feature/topic_garden_enabled';
import { useOutbox } from '@/modules/outbox';
import ResetSnowballModal from '../../components/ResetSnowballModal';

const SnowballMenu: React.FC = () => {
  const { loading, hasUserId, claims } = useUserContext();
  const { features } = useAppConfig();
  const { sends, openOutbox } = useOutbox();
  const location = useLocation();
  const router = useIonRouter();
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showResetSnowballModal, setShowResetSnowballModal] = useState(false);

  if (loading) {
    return null;
  }

  const handleAddressBookClick = () => {
    setShowActionSheet(false);
    router.push('/ab/lists/all/contacts', 'forward', 'push');
  };

  const handleUserSettingsClick = () => {
    setShowActionSheet(false);
    router.push('/settings', 'forward', 'push');
  };

  // The outbox is where a garden share is sent from; it stays reachable while
  // anything is in it, whatever the flag says now.
  const showOutbox = topicGardenEnabled(features) || sends.length > 0;

  return (
    <>
      <IonButton
        onClick={() => setShowActionSheet(true)}
        fill="clear"
        title="Snowball Menu"
        data-cy="snowball-menu"
        data-profile-state={hasUserId ? 'linked' : 'guest'}
        id="snowball-menu-button"
      >
        <div style={{ position: 'relative', width: '32px', height: '32px' }}>
          <img
            src="/images/logo_transpbg_left_87.png"
            alt="Snowball Menu"
            style={{ width: '32px', height: '32px', display: 'block' }}
          />
          {features.betaTesting && (
            <span
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                fontSize: pxToRem(7),
                fontWeight: 700,
                lineHeight: 1,
                color: '#174a7c',
                letterSpacing: '0.03em',
                pointerEvents: 'none',
              }}
            >
              BETA
            </span>
          )}
        </div>
      </IonButton>

      <IonActionSheet
        isOpen={showActionSheet}
        onDidDismiss={() => setShowActionSheet(false)}
        buttons={[
          ...(showOutbox
            ? [
                {
                  text: 'Outbox',
                  icon: paperPlaneOutline,
                  handler: () => {
                    setShowActionSheet(false);
                    openOutbox();
                  },
                },
              ]
            : []),
          {
            text: 'Address Book',
            icon: peopleOutline,
            handler: handleAddressBookClick,
          },
          {
            text: 'My Profile',
            icon: ClaimsLogic.isPhoneOrEmailVerified(claims)
              ? personCircleOutline
              : personOutline,
            handler: () => {
              setShowActionSheet(false);
              const currentPath =
                router.routeInfo?.pathname || location.pathname || '/';
              router.push('/profile/', 'forward', 'push', {
                from: currentPath,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any);
            },
          },
          {
            text: 'Settings',
            icon: settingsOutline,
            handler: handleUserSettingsClick,
          },
          {
            text: 'Reset Snowball',
            role: 'destructive',
            icon: logOutOutline,
            handler: () => {
              setShowResetSnowballModal(true);
            },
          },
          {
            text: 'Help',
            icon: helpCircleOutline,
            handler: () => {
              setShowActionSheet(false);
              router.push('/help', 'forward', 'push');
            },
          },
          {
            text: 'About',
            icon: informationCircleOutline,
            handler: () => {
              setShowActionSheet(false);
              router.push('/about', 'forward', 'push');
            },
          },
          {
            text: 'Cancel',
            role: 'cancel',
          },
        ]}
      />

      <ResetSnowballModal
        isOpen={showResetSnowballModal}
        onClose={() => setShowResetSnowballModal(false)}
      />
    </>
  );
};

export default SnowballMenu;
